import { useCallback, useEffect, useMemo, useState } from 'react'
import { PermissionPolicyOption } from '../../../../domain/value-object/PermissionPolicy'
import {
  ApplicationDirectoryDto,
  DEPRECATED_FUNCTION_DISPLAY_ORDER,
  getAllFunctions,
} from '../../../../infrastructure/api/application-functions'
import { ApplicationFunctionPermissionRow, ScopeMap } from '../types'
import { generateUuid } from '../../../../utils/uuids'
import { ScopeVO } from '../../../../domain/value-object/ScopeVO'
import functions from '../..'

type UsePermissionPolicyDataProps = {
  permissionPolicyOptions: PermissionPolicyOption[]
}

type UsePermissionPolicyDataOutput = {
  rowData: ApplicationFunctionPermissionRow[]
  scopeMap: ScopeMap
  fetchAllFunctions: () => Promise<void>
}

/**
 * application_functionsとpermission_policiesからグリッド用のデータを生成する
 * 権限でフィルターされていない全機能一覧を使用する
 */
export const usePermissionPolicyData = ({
  permissionPolicyOptions,
}: UsePermissionPolicyDataProps): UsePermissionPolicyDataOutput => {
  const [directories, setDirectories] = useState<ApplicationDirectoryDto[]>([])

  const fetchAllFunctions = useCallback(async () => {
    const response = await getAllFunctions()
    setDirectories(response.directories)
  }, [])

  useEffect(() => {
    fetchAllFunctions()
  }, [fetchAllFunctions])

  const { rowData, scopeMap } = useMemo(() => {
    // スコープマップを作成（パフォーマンス最適化）
    const scopeMap: ScopeMap = {}
    permissionPolicyOptions.forEach(policy => {
      scopeMap[policy.uuid] = {}
      policy.scopes.forEach(scope => {
        if (scope.applicationFunction) {
          scopeMap[policy.uuid][scope.applicationFunction.externalId] = scope
        }
      })
    })

    // directoriesからapplication_functionsを抽出して行データを作成
    const rows: ApplicationFunctionPermissionRow[] = []

    const extractFunctions = (
      dir: ApplicationDirectoryDto,
      parentDirectoryName?: string,
      isFirstLevel: boolean = false
    ) => {
      // 1階層目のディレクトリ名は表示しない
      const currentDirectoryName = isFirstLevel
        ? undefined
        : parentDirectoryName
          ? `${parentDirectoryName} > ${dir.name}`
          : dir.name

      // 現在のディレクトリのfunctions
      if (dir.functions && dir.functions.length > 0) {
        dir.functions.forEach(func => {
          if (
            (func.hidden &&
              func.displayOrder === DEPRECATED_FUNCTION_DISPLAY_ORDER) ||
            !functions[func.externalId] // feature-flagで非表示にされる機能を除くための条件
          ) {
            return
          }

          const scopeByPermissionPolicyUuid: {
            [permissionPolicyUuid: string]: ScopeVO
          } = {}

          // 各権限ポリシーのスコープをマップに追加
          permissionPolicyOptions.forEach(policy => {
            const scope = scopeMap[policy.uuid][func.externalId]
            if (scope) {
              scopeByPermissionPolicyUuid[policy.uuid] = scope
            }
          })

          const row: ApplicationFunctionPermissionRow = {
            uuid: generateUuid(),
            applicationFunctionExternalId: func.externalId,
            applicationFunctionName: func.name,
            directoryName: currentDirectoryName,
            scopeByPermissionPolicyUuid,
          }

          rows.push(row)
        })
      }

      // 子ディレクトリを再帰的に処理
      if (dir.directories && dir.directories.length > 0) {
        dir.directories.forEach(childDir => {
          // 1階層目の場合は親ディレクトリ名を渡さない
          extractFunctions(
            childDir,
            isFirstLevel ? undefined : currentDirectoryName,
            false
          )
        })
      }
    }

    directories.forEach(dir => extractFunctions(dir, undefined, true))

    // rowNumberを設定 (index + 1)
    rows.forEach((row, index) => {
      row.rowNumber = index + 1
    })

    return { rowData: rows, scopeMap }
  }, [directories, permissionPolicyOptions])

  return { rowData, scopeMap, fetchAllFunctions }
}
